import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { buildEventListItem } from '@/utils/eventSchema';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building, Calendar, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import YouMightLikeCity from '@/components/recommendations/YouMightLikeCity';
import { useTracking } from '@/hooks/useTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to check if event is past
const isEventPast = (event) => {
  if (!event.dates?.end) return false;
  const endDate = new Date(event.dates.end);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate < today;
};

// Filter functions
const filterUpcomingEvents = (events) => events.filter(e => !isEventPast(e));
const filterPastEvents = (events) => events.filter(e => isEventPast(e));

export default function CityEventsPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { citySlug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showPast, setShowPast] = useState(false);

  // Track page view for recommendation engine
  useTracking(city ? {
    citySlug: citySlug,
    pageType: 'city'
  } : {});

  useEffect(() => {
    const fetchCity = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/events/${citySlug}?lang=${lang}`);
        setCity(response.data.city);
      } catch (err) {
        console.error('Error fetching city:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(t('city.notFound'));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCity();
  }, [citySlug, t, lang]);

  if (notFound) {
    return (
      <MainLayout>
        <NotFoundState
          title={t('city.notFound')}
          primaryTo="/events"
          primaryLabel={t('notFoundPage.eventsCta')}
        />
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">{t('common.loading')}</span>
        </div>
      </MainLayout>
    );
  }

  if (error || !city) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <p className="text-red-600">{error || t('city.notFound')}</p>
        </div>
      </MainLayout>
    );
  }

  // Get upcoming and past events for each venue
  const venuesWithFilteredEvents = city.venues.map(venue => {
    const upcomingEvents = filterUpcomingEvents(venue.events || []);
    const pastEvents = filterPastEvents(venue.events || []);
    return {
      ...venue,
      displayEvents: showPast ? pastEvents : upcomingEvents,
      upcomingCount: upcomingEvents.length,
      pastCount: pastEvents.length,
    };
  });

  const totalUpcoming = venuesWithFilteredEvents.reduce((sum, venue) => sum + venue.upcomingCount, 0);
  const totalPast = venuesWithFilteredEvents.reduce((sum, venue) => sum + venue.pastCount, 0);

  // ItemList Schema for better AI/Search visibility
  const itemListSchema = city ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Business Events in ${city.name}`,
    "description": `Complete list of upcoming trade shows, exhibitions and conferences in ${city.name}`,
    "numberOfItems": totalUpcoming,
    "itemListElement": venuesWithFilteredEvents.flatMap(venue => 
      (venue.displayEvents || []).slice(0, 5).map((event, idx) => buildEventListItem(event, idx + 1))
    ).slice(0, 10)
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://events.jigsawconferences.co.uk" },
      { "@type": "ListItem", "position": 2, "name": "Events", "item": "https://events.jigsawconferences.co.uk/events" },
      { "@type": "ListItem", "position": 3, "name": city?.name || "", "item": `https://events.jigsawconferences.co.uk/events/${citySlug}` }
    ]
  };

  // E-E-A-T Author Schema for city pages
  const authorSchema = city ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `Business Events in ${city.name} - Complete Directory`,
    "author": {
      "@type": "Person",
      "name": "Jigsaw Events Team",
      "jobTitle": "Event Industry Specialists",
      "worksFor": {
        "@type": "Organization",
        "name": "Jigsaw Conferences",
        "url": "https://jigsawconferences.co.uk"
      }
    },
    "publisher": {
      "@type": "Organization",
      "name": "Jigsaw Events Hub",
      "logo": {
        "@type": "ImageObject",
        "url": "https://events.jigsawconferences.co.uk/logo.png"
      }
    },
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": `https://events.jigsawconferences.co.uk/events/${citySlug}`
  } : null;

  // Speakable schema for voice search
  const speakableSchema = city ? {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Business Events in ${city.name}`,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".city-intro", ".venue-count"]
    },
    "url": `https://events.jigsawconferences.co.uk/events/${citySlug}`
  } : null;

  return (
    <MainLayout>
      <SEOHead
        title={`${city.name} Business Events ${new Date().getFullYear()} | ${totalUpcoming} Trade Shows & Conferences`}
        description={`Discover ${totalUpcoming} upcoming business events in ${city.name} including trade shows, exhibitions and conferences. Browse ${city.venues.length} venues. Free group accommodation available.`}
        url={`https://events.jigsawconferences.co.uk/events/${citySlug}`}
      />
      
      {/* Structured Data for AI visibility */}
      {itemListSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {authorSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }} />}
      {speakableSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableSchema) }} />}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="city-events-title">
              {t('city.heroTitle', { city: city.name })}
            </h1>
            <p className="text-xl text-gray-600 mb-8">{city.intro}</p>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-6 text-gray-700">
                <div className="flex items-center space-x-2">
                  <Building className="w-5 h-5 text-orange-600" />
                  <span className="font-medium">{city.venues.length} {t('city.venues')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  <span className="font-medium">
                    {showPast
                      ? t('city.statPast', { count: totalPast })
                      : t('city.statUpcoming', { count: totalUpcoming })}
                  </span>
                </div>
              </div>
              
              {totalPast > 0 && (
                <Button
                  onClick={() => setShowPast(!showPast)}
                  variant="outline"
                  className="border-gray-300 hover:border-orange-600"
                  data-testid="toggle-past-events"
                >
                  {showPast ? t('city.showUpcoming') : t('city.viewPastEvents', { count: totalPast })}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Venues Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('city.venuesEvents')}</h2>

          {venuesWithFilteredEvents.every(v => v.displayEvents.length === 0) ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                {showPast ? t('city.noPast') : t('city.noUpcoming')}
              </p>
              {totalPast > 0 && !showPast && (
                <Button
                  onClick={() => setShowPast(true)}
                  variant="outline"
                  className="mt-4 border-orange-600 text-orange-600"
                >
                  {t('city.viewPastEvents', { count: totalPast })}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {venuesWithFilteredEvents.map((venue) => {
                if (venue.displayEvents.length === 0) return null;

                return (
                  <Card key={venue.slug} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow" data-testid={`venue-card-${venue.slug}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-2xl text-gray-900 mb-2">{venue.name}</CardTitle>
                          <CardDescription className="text-gray-600">
                            {showPast
                              ? t('city.venuePast', { count: venue.displayEvents.length })
                              : t('city.venueUpcoming', { count: venue.displayEvents.length })}
                          </CardDescription>
                        </div>
                        <LocalizedLink to={`/events/${citySlug}/${venue.slug}`}>
                          <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                            {t('city.viewEvents')}
                          </Button>
                        </LocalizedLink>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-6">{venue.intro}</p>

                      {/* Event Preview Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {venue.displayEvents.map((event) => (
                          <LocalizedLink
                            key={event.slug}
                            to={`/events/${citySlug}/${venue.slug}/${event.slug}`}
                            className="block"
                            data-testid={`event-preview-${event.slug}`}
                          >
                            <div className="p-4 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                              <h4 className="font-semibold text-gray-900 mb-2">{event.name}</h4>
                              <p className="text-sm text-gray-600 mb-3">{event.shortDescription}</p>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">{event.dates?.display}</span>
                                <span className="text-orange-600 font-medium flex items-center">
                                  {t('city.viewDetails')} <ArrowRight className="w-3 h-3 ml-1" />
                                </span>
                              </div>
                            </div>
                          </LocalizedLink>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* You Might Like Section */}
      {city && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <YouMightLikeCity 
              citySlug={citySlug}
              cityName={city.name}
              limit={4}
            />
          </div>
        </section>
      )}

      {/* Event Boost CTA */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{t('city.shareEvents', { city: city.name })}</p>
                <p className="text-sm text-gray-600">{t('city.shareDesc')}</p>
              </div>
            </div>
            <LocalizedLink to="/boost/dashboard">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white whitespace-nowrap">
                {t('city.startSharing')}
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('city.bringTeam', { city: city.name })}</h2>
          <p className="text-xl mb-8 text-orange-100">
            {t('city.bringDesc')}
          </p>
          <Button
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg font-medium"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="scroll-to-top-button"
          >
            {t('city.getStarted')}
          </Button>
        </div>
      </section>
    </MainLayout>
  );
}
